import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  BrowserProvider,
  Contract,
  JsonRpcProvider,
  formatUnits,
} from "ethers";

import {
  useAppKitAccount,
  useAppKitProvider,
  useAppKitNetwork,
} from "@reown/appkit/react";


/* =========================================================
   CONFIG
========================================================= */

const BSC_CHAIN_ID = 56;

const BSC_RPC =
  process.env.REACT_APP_BSC_RPC_URL ||
  "https://bsc-dataseed.bnbchain.org";

const CONTRACT_ADDRESS =
  process.env.REACT_APP_KIND_RUSH_CONTRACT_ADDRESS ||
  "0x900ADA53B342ebdF097465cefDD65Ac13Cb928f6";


/* =========================================================
   ABI
========================================================= */

const ABI = [
  "function getActiveCampaignId() view returns (uint256)",

  `function getGlobalView(uint256 campaignId)
    view
    returns (
      (
        uint256 campaignId,
        uint256 totalParticipants,
        uint256 totalQualified,
        uint256 totalCompleted,
        uint256 totalDisqualified,
        uint256 totalVerifiedTrades,
        uint256 totalKindBought,
        uint256 totalTradeVolumeUsd,
        uint256 totalQualifyingUsd,
        uint256 totalRewardUsd,
        uint256 totalClaimableRewardUsd,
        uint256 totalClaimedKind,
        uint256 rewardPoolKind,
        uint256 tier100Count,
        uint256 tier150Count,
        uint256 tier200Count,
        uint256 tier300Count,
        uint256 kindPriceUsd,
        uint256 startTime,
        uint256 endTime,
        uint256 claimEndTime,
        uint256 qualificationPeriod,
        uint256 claimCooldown,
        uint256 minPurchaseUsd,
        uint256 maxPurchaseUsd,
        uint256 claimCount,
        bool activated,
        bool closed
      ) v
    )`,

  `function getUserView(
      uint256 campaignId,
      address wallet
    )
    view
    returns (
      (
        uint8 status,
        uint8 reason,
        uint256 firstPurchaseTime,
        uint256 qualificationEnd,
        uint256 qualifyingPurchaseUsd,
        uint256 frozenPurchaseUsd,
        uint256 tradeVolumeUsd,
        uint256 rewardBps,
        uint256 rewardUsd,
        uint256 claimedKind,
        uint256 claimsCompleted,
        uint256 claimCount,
        uint256 tradeCount,
        uint256 nextClaimTime,
        uint256 timeUntilNextClaim,
        uint256 nextClaimUsd,
        uint256 nextClaimKind,
        uint256 currentKindBalance,
        bool qualificationFinished,
        bool claimReady
      ) v
    )`,

  `function getLeaderboard(
      uint256 campaignId,
      uint256 offset,
      uint256 limit
    )
    view
    returns (
      (
        address wallet,
        uint8 status,
        uint8 reason,
        uint256 qualifyingUsd,
        uint256 tradeVolumeUsd,
        uint256 rewardUsd,
        uint256 claimsCompleted,
        uint256 tradeCount
      )[] rows,
      uint256 total
    )`,

  `function getUserTrades(
      uint256 campaignId,
      address wallet,
      uint256 offset,
      uint256 limit
    )
    view
    returns (
      (
        bytes32 tradeId,
        address wallet,
        address pair,
        uint256 kindAmount,
        uint256 usdValue,
        uint256 fullUsdValue,
        uint256 timestamp,
        bool verified
      )[] records,
      uint256 total
    )`,

  "function claim(uint256 campaignId)",

  "function getCompactKindPrice(uint256 campaignId) view returns (uint256)",
];


/* =========================================================
   ENUM LABELS
========================================================= */

const STATUS = {
  0: "Not Registered",
  1: "Qualifying",
  2: "Active",
  3: "Completed",
  4: "Disqualified",
};

const REASON = {
  0: "None",
  1: "KIND Sold",
  2: "KIND Sent",
  3: "KIND Received",
  4: "Invalid Trade",
  5: "Unauthorized Contract",
  6: "Admin Emergency",
};


/* =========================================================
   HELPERS
========================================================= */

const ZERO = 0n;


const usd = (value, digits = 2) => {
  try {
    return Number(
      formatUnits(
        value ?? ZERO,
        18
      )
    ).toLocaleString(
      undefined,
      {
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
      }
    );
  }
  catch {
    return "0.00";
  }
};


const token = (value, digits = 2) => {
  try {
    return Number(
      formatUnits(
        value ?? ZERO,
        18
      )
    ).toLocaleString(
      undefined,
      {
        maximumFractionDigits: digits,
      }
    );
  }
  catch {
    return "0";
  }
};


/* =========================================================
   FRONTEND REWARD CALCULATION

   Uses qualifying USD only.

   $0.01 - $50.00      = 100%
   $50.01 - $100.00    = 150%
   $100.01 - $150.00   = 200%
   $150.01 - $200.00   = 300%
========================================================= */

const getRewardRate = (qualifyingUsd) => {
  try {
    const amount =
      qualifyingUsd ??
      ZERO;

    const USD_50 =
      50n *
      10n ** 18n;

    const USD_100 =
      100n *
      10n ** 18n;

    const USD_150 =
      150n *
      10n ** 18n;

    if (
      amount <=
      ZERO
    ) {
      return 0;
    }

    if (
      amount <=
      USD_50
    ) {
      return 100;
    }

    if (
      amount <=
      USD_100
    ) {
      return 150;
    }

    if (
      amount <=
      USD_150
    ) {
      return 200;
    }

    return 300;
  }
  catch {
    return 0;
  }
};


const getCalculatedReward = (qualifyingUsd) => {
  try {
    const amount = Number(
      formatUnits(
        qualifyingUsd ?? ZERO,
        18
      )
    );

    if (
      !Number.isFinite(amount) ||
      amount <= 0
    ) {
      return 0;
    }

    const rate =
      getRewardRate(
        qualifyingUsd
      );

    return (
      amount *
      rate
    ) / 100;
  }
  catch {
    return 0;
  }
};

/* =========================================================
   KIND -> USD DISPLAY CALCULATION

   kindAmount and kindPriceUsd both use 18 decimals.
========================================================= */

const getKindUsdValue = (
  kindAmount,
  kindPriceUsd
) => {
  try {
    const amount =
      kindAmount ??
      ZERO;

    const price =
      kindPriceUsd ??
      ZERO;

    if (
      amount <=
        ZERO ||
      price <=
        ZERO
    ) {
      return ZERO;
    }

    return (
      amount *
      price
    ) /
      (
        10n **
        18n
      );
  }
  catch {
    return ZERO;
  }
};


const kindWithUsd = (
  kindAmount,
  kindPriceUsd,
  digits = 2
) => {
  return `${token(
    kindAmount,
    digits
  )} KIND ($${usd(
    getKindUsdValue(
      kindAmount,
      kindPriceUsd
    )
  )})`;
};


const shortAddress = (address) => {
  if (!address) return "—";

  return `${address.slice(0, 6)}...${address.slice(-4)}`;
};





const dateText = (timestamp) => {
  const value = Number(timestamp || 0);

  if (!value) {
    return "—";
  }

  return new Date(
    value * 1000
  ).toLocaleString();
};


const duration = (seconds) => {
  let total = Math.max(
    0,
    Number(seconds || 0)
  );

  const days =
    Math.floor(
      total / 86400
    );

  total %= 86400;

  const hours =
    Math.floor(
      total / 3600
    );

  total %= 3600;

  const minutes =
    Math.floor(
      total / 60
    );

  const secs =
    Math.floor(
      total % 60
    );

  if (days > 0) {
    return `${days}d ${hours}h ${minutes}m ${secs}s`;
  }

  return `${String(hours).padStart(2, "0")}:${String(
    minutes
  ).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
};


const errorMessage = (error) => {
  return (
    error?.shortMessage ||
    error?.reason ||
    error?.message ||
    "Transaction failed"
  );
};


/* =========================================================
   COMPONENT
========================================================= */

export default function RushDashboard() {
  const {
    address,
    isConnected,
  } = useAppKitAccount();

  const {
    walletProvider,
  } = useAppKitProvider(
    "eip155"
  );

  const {
    chainId,
  } = useAppKitNetwork();


  /* =======================================================
     STATE
  ======================================================= */

  const [
    campaignId,
    setCampaignId,
  ] = useState(ZERO);

  const [
    globalData,
    setGlobalData,
  ] = useState(null);

  const [
    userData,
    setUserData,
  ] = useState(null);

  const [
    leaderboard,
    setLeaderboard,
  ] = useState([]);

  const [
    leaderboardTotal,
    setLeaderboardTotal,
  ] = useState(0);

  const [
    selectedWallet,
    setSelectedWallet,
  ] = useState("");

  const [
    selectedTrades,
    setSelectedTrades,
  ] = useState([]);

  const [
    tradeModalOpen,
    setTradeModalOpen,
  ] = useState(false);

  const [
    loading,
    setLoading,
  ] = useState(true);

  const [
    claimLoading,
    setClaimLoading,
  ] = useState(false);

  const [
    message,
    setMessage,
  ] = useState("");

  const [
    now,
    setNow,
  ] = useState(
    Math.floor(
      Date.now() / 1000
    )
  );


  /* =======================================================
     READ PROVIDER
  ======================================================= */

  const readProvider =
    useMemo(
      () => {
        return new JsonRpcProvider(
          BSC_RPC
        );
      },
      []
    );


  const readContract =
    useMemo(
      () => {
        if (
          !CONTRACT_ADDRESS ||
          CONTRACT_ADDRESS.includes(
            "PASTE_NEW"
          )
        ) {
          return null;
        }

        return new Contract(
          CONTRACT_ADDRESS,
          ABI,
          readProvider
        );
      },
      [
        readProvider,
      ]
    );


  /* =======================================================
     LOCAL CLOCK
  ======================================================= */

  useEffect(
    () => {
      const timer =
        setInterval(
          () => {
            setNow(
              Math.floor(
                Date.now() /
                  1000
              )
            );
          },
          1000
        );

      return () =>
        clearInterval(
          timer
        );
    },
    []
  );


  /* =======================================================
     LOAD LEADERBOARD
  ======================================================= */

  const loadLeaderboard =
    useCallback(
      async (
        contract,
        id
      ) => {
        const allRows = [];

        let offset = 0;

        const pageSize = 100;

        let total = 1;


        while (
          offset < total
        ) {
          const result =
            await contract.getLeaderboard(
              id,
              offset,
              pageSize
            );

          const rows =
            result[0] || [];

          total =
            Number(
              result[1] || 0
            );


          rows.forEach(
            (row) => {
              allRows.push({
                wallet:
                  row.wallet,

                status:
                  Number(
                    row.status
                  ),

                reason:
                  Number(
                    row.reason
                  ),

                qualifyingUsd:
                  row.qualifyingUsd,

                tradeVolumeUsd:
                  row.tradeVolumeUsd,

                rewardUsd:
                  row.rewardUsd,

                claimsCompleted:
                  Number(
                    row.claimsCompleted
                  ),

                tradeCount:
                  Number(
                    row.tradeCount
                  ),
              });
            }
          );


          if (
            rows.length === 0
          ) {
            break;
          }


          offset +=
            rows.length;
        }


        /*
         * Sort leaderboard by FULL TRADE VOLUME.
         */
        allRows.sort(
          (a, b) => {
            if (
              a.tradeVolumeUsd ===
              b.tradeVolumeUsd
            ) {
              return 0;
            }

            return (
              a.tradeVolumeUsd >
              b.tradeVolumeUsd
                ? -1
                : 1
            );
          }
        );


        setLeaderboard(
          allRows
        );

        setLeaderboardTotal(
          total
        );
      },
      []
    );


  /* =======================================================
     MAIN LOAD
  ======================================================= */

  const loadDashboard =
    useCallback(
      async (
        showLoader = false
      ) => {
        if (
          !readContract
        ) {
          setLoading(false);

          return;
        }


        try {
          if (showLoader) {
            setLoading(true);
          }


          const activeId =
            await readContract.getActiveCampaignId();


          setCampaignId(
            activeId
          );


          if (
            activeId === ZERO
          ) {
            setGlobalData(
              null
            );

            setUserData(
              null
            );

            setLeaderboard(
              []
            );

            return;
          }


          const global =
            await readContract.getGlobalView(
              activeId
            );


          setGlobalData(
            global
          );


          if (
            address
          ) {
            const user =
              await readContract.getUserView(
                activeId,
                address
              );

            setUserData(
              user
            );
          }
          else {
            setUserData(
              null
            );
          }


          await loadLeaderboard(
            readContract,
            activeId
          );
        }
        catch (error) {
          console.error(
            "Dashboard load error:",
            error
          );

          setMessage(
            errorMessage(
              error
            )
          );
        }
        finally {
          setLoading(
            false
          );
        }
      },
      [
        readContract,
        address,
        loadLeaderboard,
      ]
    );


  /* =======================================================
     INITIAL + AUTO REFRESH
  ======================================================= */

  useEffect(
    () => {
      loadDashboard(
        true
      );


      const timer =
        setInterval(
          () => {
            loadDashboard(
              false
            );
          },
          12000
        );


      return () =>
        clearInterval(
          timer
        );
    },
    [
      loadDashboard,
    ]
  );


  /* =======================================================
     USER STATUS FLAGS
  ======================================================= */

  const isDisqualified =
    useMemo(
      () =>
        Boolean(
          userData &&
          Number(
            userData.status
          ) === 4
        ),
      [
        userData,
      ]
    );


  /* =======================================================
     LIVE REWARD TIER COUNTS

     Uses the leaderboard qualifying amount, not the stored
     finalized tier counters from getGlobalView().

     Disqualified wallets are excluded immediately.
  ======================================================= */

  const liveTierCounts =
    useMemo(
      () => {
        const counts = {
          tier100: 0,
          tier150: 0,
          tier200: 0,
          tier300: 0,
        };

        const USD_50 =
          50n *
          10n ** 18n;

        const USD_100 =
          100n *
          10n ** 18n;

        const USD_150 =
          150n *
          10n ** 18n;

        const USD_200 =
          200n *
          10n ** 18n;

        leaderboard.forEach(
          (row) => {
            // Do not count disqualified wallets.
            if (
              Number(
                row.status
              ) === 4
            ) {
              return;
            }

            const amount =
              row.qualifyingUsd ??
              ZERO;

            if (
              amount <= ZERO
            ) {
              return;
            }

            if (
              amount <= USD_50
            ) {
              counts.tier100 += 1;
              return;
            }

            if (
              amount <= USD_100
            ) {
              counts.tier150 += 1;
              return;
            }

            if (
              amount <= USD_150
            ) {
              counts.tier200 += 1;
              return;
            }

            if (
              amount <= USD_200
            ) {
              counts.tier300 += 1;
            }
          }
        );

        return counts;
      },
      [
        leaderboard,
      ]
    );

const formatNumberUsd = (value) => {
  const amount =
    Number(value || 0);

  return amount.toLocaleString(
    undefined,
    {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }
  );
};

  /* =======================================================
     DYNAMIC QUALIFICATION TIMER
  ======================================================= */

  const qualificationRemaining =
    useMemo(
      () => {
        if (
          !userData ||
          isDisqualified
        ) {
          return 0;
        }


        const end =
          Number(
            userData.qualificationEnd ||
              0
          );


        return Math.max(
          0,
          end - now
        );
      },
      [
        userData,
        isDisqualified,
        now,
      ]
    );


  /* =======================================================
     DYNAMIC NEXT CLAIM TIMER
  ======================================================= */

  const nextClaimRemaining =
    useMemo(
      () => {
        if (
          !userData ||
          isDisqualified
        ) {
          return 0;
        }


        if (
          Number(
            userData.claimsCompleted
          ) === 0
        ) {
          return qualificationRemaining;
        }


        const end =
          Number(
            userData.nextClaimTime ||
              0
          );


        return Math.max(
          0,
          end - now
        );
      },
      [
        userData,
        isDisqualified,
        qualificationRemaining,
        now,
      ]
    );


  /* =======================================================
     CLAIM READY DYNAMIC
  ======================================================= */

  const claimReady =
    useMemo(
      () => {
        if (
          !userData ||
          !globalData
        ) {
          return false;
        }


        if (
          globalData.closed
        ) {
          return false;
        }


        if (
          Number(
            userData.status
          ) === 4 ||
          Number(
            userData.status
          ) === 3
        ) {
          return false;
        }


        return Boolean(
          userData.claimReady
        );
      },
      [
        userData,
        globalData,
      ]
    );


  /* =======================================================
     CLAIM
  ======================================================= */

  const handleClaim =
    useCallback(
      async () => {
        if (
          !walletProvider ||
          !address ||
          campaignId === ZERO
        ) {
          setMessage(
            "Connect your wallet first."
          );

          return;
        }


        try {
          setClaimLoading(
            true
          );

          setMessage(
            ""
          );


          const currentChain =
            Number(
              chainId
            );


          if (
            currentChain !==
            BSC_CHAIN_ID
          ) {
            setMessage(
              "Please switch to BSC Testnet."
            );

            return;
          }


          const provider =
            new BrowserProvider(
              walletProvider
            );


          const signer =
            await provider.getSigner();


          const contract =
            new Contract(
              CONTRACT_ADDRESS,
              ABI,
              signer
            );


          const tx =
            await contract.claim(
              campaignId
            );


          setMessage(
            "Claim submitted..."
          );


          await tx.wait();


          setMessage(
            "Claim successful."
          );


          await loadDashboard(
            false
          );
        }
        catch (error) {
          console.error(
            error
          );

          setMessage(
            errorMessage(
              error
            )
          );
        }
        finally {
          setClaimLoading(
            false
          );
        }
      },
      [
        walletProvider,
        address,
        campaignId,
        chainId,
        loadDashboard,
      ]
    );


  /* =======================================================
     LOAD USER TRADES
  ======================================================= */

  const openTrades =
    useCallback(
      async (
        wallet
      ) => {
        if (
          !readContract ||
          campaignId === ZERO
        ) {
          return;
        }


        try {
          setSelectedWallet(
            wallet
          );

          setSelectedTrades(
            []
          );

          setTradeModalOpen(
            true
          );


          const allTrades = [];

          let offset = 0;

          const limit = 100;

          let total = 1;


          while (
            offset < total
          ) {
            const result =
              await readContract.getUserTrades(
                campaignId,
                wallet,
                offset,
                limit
              );


            const records =
              result[0] || [];

            total =
              Number(
                result[1] || 0
              );


            records.forEach(
              (trade) => {
                allTrades.push({
                  tradeId:
                    trade.tradeId,

                  wallet:
                    trade.wallet,

                  pair:
                    trade.pair,

                  kindAmount:
                    trade.kindAmount,

                  usdValue:
                    trade.usdValue,

                  fullUsdValue:
                    trade.fullUsdValue,

                  timestamp:
                    trade.timestamp,

                  verified:
                    trade.verified,
                });
              }
            );


            if (
              records.length === 0
            ) {
              break;
            }


            offset +=
              records.length;
          }


          allTrades.sort(
            (a, b) =>
              Number(
                b.timestamp
              ) -
              Number(
                a.timestamp
              )
          );


          setSelectedTrades(
            allTrades
          );
        }
        catch (error) {
          console.error(
            error
          );

          setMessage(
            errorMessage(
              error
            )
          );
        }
      },
      [
        readContract,
        campaignId,
      ]
    );


  /* =======================================================
     UI STATE
  ======================================================= */

  if (
    !CONTRACT_ADDRESS ||
    CONTRACT_ADDRESS.includes(
      "PASTE_NEW"
    )
  ) {
    return (
      <div style={styles.page}>
        <div style={styles.empty}>
          <h2>
            Contract address required
          </h2>

          <p>
            Add your newly deployed contract address to
            CONTRACT_ADDRESS.
          </p>
        </div>
      </div>
    );
  }


  if (loading) {
    return (
      <div style={styles.page}>
        <div style={styles.empty}>
          Loading campaign...
        </div>
      </div>
    );
  }


  if (
    campaignId === ZERO ||
    !globalData
  ) {
    return (
      <div style={styles.page}>
        <div style={styles.empty}>
          <div style={styles.emptyIcon}>
            ⚡
          </div>

          <h2>
            No Active Campaign
          </h2>

          <p>
            There is currently no activated and open campaign.
          </p>
        </div>
      </div>
    );
  }


  /* =======================================================
     RENDER
  ======================================================= */

  return (
    <div style={styles.page}>
      <div style={styles.container}>

        {/* HEADER */}

        <div style={styles.header}>
          <div>
            <div style={styles.eyebrow}>
              KIND CAT TOKEN
            </div>

            <h1 style={styles.title}>
             Kind Bonus Rush
            </h1>

            <div style={styles.subTitle}>
              Campaign #{campaignId.toString()}
            </div>
          </div>

          <div style={styles.headerRight}>
            <div style={styles.liveBadge}>
              <span style={styles.liveDot} />

              {globalData.closed
                ? "Closed"
                : "Live"}
            </div>

            <button
              style={styles.refreshButton}
              onClick={() =>
                loadDashboard(
                  true
                )
              }
            >
              Refresh
            </button>
          </div>
        </div>


        {/* GLOBAL CARDS */}

        <div style={styles.statGrid}>
          <StatCard
            label="Participants"
            value={Number(
              globalData.totalParticipants
            ).toLocaleString()}
          />

          <StatCard
            label="Verified Trades"
            value={Number(
              globalData.totalVerifiedTrades
            ).toLocaleString()}
          />

          <StatCard
            label="Total Trade Volume"
            value={`$${usd(
              globalData.totalTradeVolumeUsd
            )}`}
          />

          <StatCard
            label="Total Qualifying"
            value={`$${usd(
              globalData.totalQualifyingUsd
            )}`}
          />

          <StatCard
            label="Total Rewards"
            value={`$${usd(
              globalData.totalRewardUsd
            )}`}
          />

          <StatCard
            label="Reward Pool"
            value={kindWithUsd(
              globalData.rewardPoolKind,
              globalData.kindPriceUsd
            )}
          />

          <StatCard
            label="KIND Price"
            value={`$${usd(
              globalData.kindPriceUsd,
              8
            )}`}
          />

          <StatCard
            label="Completed"
            value={Number(
              globalData.totalCompleted
            ).toLocaleString()}
          />
        </div>


        {/* PROFILE */}

        <section style={styles.section}>
          <div style={styles.sectionHeader}>
            <div>
              <div style={styles.sectionEyebrow}>
                YOUR ACCOUNT
              </div>

              <h2 style={styles.sectionTitle}>
                Profile Overview
              </h2>
            </div>

            {address && (
              <div style={styles.walletBadge}>
                {shortAddress(
                  address
                )}
              </div>
            )}
          </div>


          {!isConnected ||
          !address ? (
            <div style={styles.profileEmpty}>
              Connect your wallet to view your campaign profile.
            </div>
          ) : !userData ||
            Number(
              userData.status
            ) === 0 ? (
            <div style={styles.profileEmpty}>
              No campaign participation found for this wallet.
            </div>
          ) : (
            <>
              <div style={styles.profileTop}>
                <div>
                  <div style={styles.smallLabel}>
                    STATUS
                  </div>

                  <div
                    style={{
                      ...styles.statusText,
                      ...(Number(
                        userData.status
                      ) === 4
                        ? styles.statusBad
                        : styles.statusGood),
                    }}
                  >
                    {
                      STATUS[
                        Number(
                          userData.status
                        )
                      ]
                    }
                  </div>
                </div>

                <div>
                  <div style={styles.smallLabel}>
                    REWARD TIER
                  </div>

                  <div style={styles.largeValue}>
                    {isDisqualified
                      ? "Disqualified"
                      : `${getRewardRate(
                          userData.qualifyingPurchaseUsd
                        )}%`}
                  </div>
                </div>

                <div>
                  <div style={styles.smallLabel}>
                    CLAIMS
                  </div>

                  <div style={styles.largeValue}>
                    {Number(
                      userData.claimsCompleted
                    )}{" "}
                    /{" "}
                    {Number(
                      userData.claimCount
                    )}
                  </div>
                </div>
              </div>


              <div style={styles.profileGrid}>
                <DataBox
                  label="Qualifying Purchase"
                  value={`$${usd(
                    userData.qualifyingPurchaseUsd
                  )}`}
                />

                <DataBox
                  label="Total Trade Volume"
                  value={`$${usd(
                    userData.tradeVolumeUsd
                  )}`}
                />

                <DataBox
                  label="Frozen Purchase"
                  value={`$${usd(
                    userData.frozenPurchaseUsd
                  )}`}
                />

                <DataBox
                  label="Total Reward"
                  value={`$${usd(
                    getCalculatedReward(
                      userData.qualifyingPurchaseUsd
                    )
                  )}`}
                />

                <DataBox
                  label="Claimed KIND"
                  value={kindWithUsd(
                    userData.claimedKind,
                    globalData.kindPriceUsd
                  )}
                />

                <DataBox
                  label="Wallet KIND"
                  value={kindWithUsd(
                    userData.currentKindBalance,
                    globalData.kindPriceUsd
                  )}
                />

                <DataBox
                  label="Verified Trades"
                  value={Number(
                    userData.tradeCount
                  ).toLocaleString()}
                />

                <DataBox
                  label="Disqualification"
                  value={
                    REASON[
                      Number(
                        userData.reason
                      )
                    ]
                  }
                />
              </div>


              {isDisqualified ? (
                <div
                  style={{
                    ...styles.claimBox,
                    ...styles.claimBoxDisqualified,
                  }}
                >
                  <div>
                    <div style={styles.claimLabel}>
                      CAMPAIGN STATUS
                    </div>

                    <div
                      style={{
                        ...styles.claimTimer,
                        ...styles.disqualifiedText,
                      }}
                    >
                      DISQUALIFIED
                    </div>

                    <div style={styles.claimMeta}>
                      Reason: {
                        REASON[
                          Number(
                            userData.reason
                          )
                        ] || "Disqualified"
                      }
                    </div>

                    <div style={styles.claimStopped}>
                      Qualification timer stopped. Claims are permanently unavailable for this campaign.
                    </div>
                  </div>


                  <div style={styles.claimReward}>
                    <div style={styles.claimLabel}>
                      NEXT REWARD
                    </div>

                    <strong style={styles.rewardUsd}>
                      $0.00
                    </strong>

                    <span style={styles.rewardKind}>
                      0 KIND
                    </span>
                  </div>


                  <button
                    style={{
                      ...styles.claimButton,
                      ...styles.claimButtonDisabled,
                    }}
                    disabled
                  >
                    Disqualified
                  </button>
                </div>
              ) : (
                <div style={styles.claimBox}>
                  <div>
                    <div style={styles.claimLabel}>
                      {Number(
                        userData.claimsCompleted
                      ) === 0
                        ? "Qualification / First Claim"
                        : "Next Claim"}
                    </div>

                    <div style={styles.claimTimer}>
                      {claimReady
                        ? "CLAIM READY"
                        : duration(
                            nextClaimRemaining
                          )}
                    </div>

                    <div style={styles.claimMeta}>
                      Qualification ends:{" "}
                      {dateText(
                        userData.qualificationEnd
                      )}
                    </div>
                  </div>


                  <div style={styles.claimReward}>
                    <div style={styles.claimLabel}>
                      NEXT REWARD
                    </div>

                    <strong style={styles.rewardUsd}>
                      $
                      {usd(
                        userData.nextClaimUsd
                      )}
                    </strong>

                    <span style={styles.rewardKind}>
                      {kindWithUsd(
                        userData.nextClaimKind,
                        globalData.kindPriceUsd
                      )}
                    </span>
                  </div>


                  <button
                    style={{
                      ...styles.claimButton,
                      ...(!claimReady ||
                      claimLoading
                        ? styles.claimButtonDisabled
                        : {}),
                    }}
                    disabled={
                      !claimReady ||
                      claimLoading
                    }
                    onClick={
                      handleClaim
                    }
                  >
                    {claimLoading
                      ? "Claiming..."
                      : claimReady
                      ? "Claim Reward"
                      : "Not Ready"}
                  </button>
                </div>
              )}
            </>
          )}
        </section>


        {/* CAMPAIGN DETAILS */}

        <section style={styles.section}>
          <div style={styles.sectionHeader}>
            <div>
              <div style={styles.sectionEyebrow}>
                CAMPAIGN
              </div>

              <h2 style={styles.sectionTitle}>
                Global Overview
              </h2>
            </div>
          </div>

          <div style={styles.detailsGrid}>
            <DataBox
              label="Campaign Start"
              value={dateText(
                globalData.startTime
              )}
            />

            <DataBox
              label="Buy Period Ends"
              value={dateText(
                globalData.endTime
              )}
            />

            <DataBox
              label="Claim Period Ends"
              value={dateText(
                globalData.claimEndTime
              )}
            />

            <DataBox
              label="Qualification"
              value={`${Number(
                globalData.qualificationPeriod
              ) /
                3600} hours`}
            />

            <DataBox
              label="Claim Cooldown"
              value={`${Number(
                globalData.claimCooldown
              ) /
                3600} hours`}
            />

            <DataBox
              label="Maximum Qualification"
              value={`$${usd(
                globalData.maxPurchaseUsd
              )}`}
            />

            <DataBox
              label="Qualified Users"
              value={Number(
                globalData.totalQualified
              ).toLocaleString()}
            />

            <DataBox
              label="Disqualified"
              value={Number(
                globalData.totalDisqualified
              ).toLocaleString()}
            />
          </div>
        </section>


        {/* TIERS */}

        <section style={styles.section}>
          <div style={styles.sectionHeader}>
            <div>
              <div style={styles.sectionEyebrow}>
                REWARDS
              </div>

              <h2 style={styles.sectionTitle}>
                Reward Tiers
              </h2>
            </div>
          </div>

          <div style={styles.tierGrid}>
            <Tier
              title="100%"
              
              users={
                liveTierCounts.tier100
              }
            />

            <Tier
              title="150%"
             
              users={
                liveTierCounts.tier150
              }
            />

            <Tier
              title="200%"
             
              users={
                liveTierCounts.tier200
              }
            />

            <Tier
              title="300%"
      
              users={
                liveTierCounts.tier300
              }
              featured
            />
          </div>
        </section>


        {/* LEADERBOARD */}

        <section style={styles.section}>
          <div style={styles.sectionHeader}>
            <div>
              <div style={styles.sectionEyebrow}>
                COMMUNITY
              </div>

              <h2 style={styles.sectionTitle}>
                Leaderboard
              </h2>
            </div>

            <div style={styles.totalBadge}>
              {leaderboardTotal} Users
            </div>
          </div>


          <div style={styles.tableWrap}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>
                    #
                  </th>

                  <th style={styles.th}>
                    Wallet
                  </th>

                  <th style={styles.th}>
                    Status
                  </th>

                  <th style={styles.th}>
                    Qualifying
                  </th>

                  <th style={styles.th}>
                    Reward Rate
                  </th>

                  <th style={styles.th}>
                    Trade Volume
                  </th>

                  <th style={styles.th}>
                    Reward
                  </th>

                  <th style={styles.th}>
                    Claims
                  </th>

                  <th style={styles.th}>
                    Trades
                  </th>

                  <th style={styles.th}>
                    History
                  </th>
                </tr>
              </thead>

              <tbody>
                {leaderboard.map(
                  (
                    row,
                    index
                  ) => (
                    <tr
                      key={
                        row.wallet
                      }
                      style={
                        styles.tr
                      }
                    >
                      <td style={styles.td}>
                        <div style={styles.rank}>
                          {index +
                            1}
                        </div>
                      </td>

                      <td style={styles.td}>
                        <span style={styles.addressText}>
                          {shortAddress(
                            row.wallet
                          )}
                        </span>
                      </td>

                      <td style={styles.td}>
                        <span
                          style={{
                            ...styles.tableStatus,
                            ...(row.status ===
                            4
                              ? styles.tableStatusBad
                              : {}),
                          }}
                        >
                          {
                            STATUS[
                              row.status
                            ]
                          }
                        </span>
                      </td>

                      <td style={styles.td}>
                        $
                        {usd(
                          row.qualifyingUsd
                        )}
                      </td>

                      <td style={styles.td}>
                        <span style={styles.rewardRateBadge}>
                          {getRewardRate(
                            row.qualifyingUsd
                          )}%
                        </span>
                      </td>

                      <td style={styles.td}>
                        <strong>
                          $
                          {usd(
                            row.tradeVolumeUsd
                          )}
                        </strong>
                      </td>

                  <td style={styles.td}>
  <strong style={styles.rewardAmount}>
    $
    {formatNumberUsd(
      getCalculatedReward(
        row.qualifyingUsd
      )
    )}
  </strong>
</td>

                      <td style={styles.td}>
                        {
                          row.claimsCompleted
                        }{" "}
                        / 4
                      </td>

                      <td style={styles.td}>
                        {
                          row.tradeCount
                        }
                      </td>

                      <td style={styles.td}>
                        <button
                          style={styles.tradeButton}
                          onClick={() =>
                            openTrades(
                              row.wallet
                            )
                          }
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  )
                )}

                {leaderboard.length ===
                  0 && (
                  <tr>
                    <td
                      colSpan="10"
                      style={styles.noRows}
                    >
                      No participants yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>


        {message && (
          <div style={styles.message}>
            {message}
          </div>
        )}
      </div>


      {/* TRADE HISTORY MODAL */}

      {tradeModalOpen && (
        <div style={styles.modalBackdrop}>
          <div style={styles.modal}>
            <div style={styles.modalHeader}>
              <div>
                <div style={styles.sectionEyebrow}>
                  VERIFIED BUY HISTORY
                </div>

                <h2 style={styles.modalTitle}>
                  {shortAddress(
                    selectedWallet
                  )}
                </h2>
              </div>

              <button
                style={styles.closeButton}
                onClick={() =>
                  setTradeModalOpen(
                    false
                  )
                }
              >
                ×
              </button>
            </div>


            <div style={styles.modalBody}>
              {selectedTrades.length ===
              0 ? (
                <div style={styles.profileEmpty}>
                  No verified trades.
                </div>
              ) : (
                selectedTrades.map(
                  (
                    trade,
                    index
                  ) => (
                    <div
                      key={`${trade.tradeId}-${index}`}
                      style={styles.tradeCard}
                    >
                      <div style={styles.tradeTop}>
                        <strong>
                          Trade #
                          {selectedTrades.length -
                            index}
                        </strong>

                        <span style={styles.verified}>
                          Verified
                        </span>
                      </div>

                      <div style={styles.tradeGrid}>
                        <TradeValue
                          label="Full USD Value"
                          value={`$${usd(
                            trade.fullUsdValue
                          )}`}
                        />

                        <TradeValue
                          label="Qualifying USD"
                          value={`$${usd(
                            trade.usdValue
                          )}`}
                        />

                        <TradeValue
                          label="KIND Amount"
                          value={kindWithUsd(
                            trade.kindAmount,
                            globalData.kindPriceUsd
                          )}
                        />

                        <TradeValue
                          label="Time"
                          value={dateText(
                            trade.timestamp
                          )}
                        />
                      </div>

                      <div style={styles.tradeId}>
                        {trade.tradeId}
                      </div>
                    </div>
                  )
                )
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


/* =========================================================
   SMALL COMPONENTS
========================================================= */

function StatCard({
  label,
  value,
}) {
  return (
    <div style={styles.statCard}>
      <div style={styles.smallLabel}>
        {label}
      </div>

      <div style={styles.statValue}>
        {value}
      </div>
    </div>
  );
}


function DataBox({
  label,
  value,
}) {
  return (
    <div style={styles.dataBox}>
      <div style={styles.smallLabel}>
        {label}
      </div>

      <div style={styles.dataValue}>
        {value}
      </div>
    </div>
  );
}


function Tier({
  title,
  range,
  users,
  featured,
}) {
  return (
    <div
      style={{
        ...styles.tierCard,
        ...(featured
          ? styles.tierFeatured
          : {}),
      }}
    >
      <div style={styles.tierPercent}>
        {title}
      </div>

      <div style={styles.tierRange}>
        {range}
      </div>

      <div style={styles.tierUsers}>
        {Number(
          users
        ).toLocaleString()}{" "}
        users
      </div>
    </div>
  );
}


function TradeValue({
  label,
  value,
}) {
  return (
    <div>
      <div style={styles.tradeLabel}>
        {label}
      </div>

      <div style={styles.tradeValue}>
        {value}
      </div>
    </div>
  );
}


/* =========================================================
   STYLES
========================================================= */

const styles = {
  page: {
    minHeight: "100vh",
    background:
      "radial-gradient(circle at top left, rgba(180, 156, 16, 0.16), transparent 34%), #080a0f",
    color: "#ffffff",
    padding: "28px 18px 70px",
    fontFamily: "Inter, Arial, sans-serif",
  },

  container: {
    maxWidth: "1440px",
    margin: "0 auto",
  },

  header: {
    display: "flex",
    justifyContent: "space-between",
    gap: "20px",
    alignItems: "center",
    marginBottom: "26px",
    flexWrap: "wrap",
  },

  eyebrow: {
    color: "#ffcc66",
    fontSize: "12px",
    fontWeight: 800,
    letterSpacing: "2px",
    marginBottom: "8px",
  },

  title: {
    margin: 0,
    fontSize: "34px",
    lineHeight: 1.1,
    fontWeight: 850,
  },

  subTitle: {
    marginTop: "9px",
    color: "#808593",
    fontSize: "14px",
  },

  headerRight: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    flexWrap: "wrap",
  },

  liveBadge: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    background: "#11151d",
    border: "1px solid #222735",
    borderRadius: "14px",
    padding: "11px 15px",
    fontSize: "13px",
    fontWeight: 700,
  },

  liveDot: {
    width: "8px",
    height: "8px",
    borderRadius: "50%",
    background: "#6cff9a",
    boxShadow: "0 0 12px rgba(108,255,154,.8)",
  },

  refreshButton: {
    background: "linear-gradient(135deg,#ffcc66,#e5ad45)",
    color: "white",
    border: 0,
    borderRadius: "14px",
    padding: "12px 18px",
    cursor: "pointer",
    fontWeight: 750,
  },

  statGrid: {
    display: "grid",
    gridTemplateColumns:
      "repeat(auto-fit, minmax(min(100%, 300px), 1fr))",
    gap: "12px",
    marginBottom: "18px",
  },

  statCard: {
    padding: "20px",
    borderRadius: "18px",
    background: "linear-gradient(145deg,#10131a,#0d1016)",
    border: "1px solid #202531",
    boxShadow: "0 12px 40px rgba(0,0,0,.18)",
    minWidth: 0,
  },

  smallLabel: {
    color: "#777e8c",
    textTransform: "uppercase",
    letterSpacing: "1px",
    fontSize: "10px",
    fontWeight: 800,
  },

  statValue: {
    marginTop: "10px",
    fontSize: "21px",
    fontWeight: 800,
    overflowWrap: "anywhere",
  },

  section: {
    background:
      "linear-gradient(145deg,rgba(17,20,28,.96),rgba(11,14,20,.96))",
    border: "1px solid #202531",
    borderRadius: "22px",
    marginTop: "18px",
    padding: "22px",
  },

  sectionHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "15px",
    marginBottom: "20px",
    flexWrap: "wrap",
  },

  sectionEyebrow: {
    fontSize: "10px",
    letterSpacing: "1.5px",
    color: "#ffcc66",
    fontWeight: 850,
  },

  sectionTitle: {
    margin: "5px 0 0",
    fontSize: "22px",
  },

  walletBadge: {
    background: "#161a24",
    border: "1px solid #282e3d",
    padding: "10px 14px",
    borderRadius: "12px",
    color: "#c9cbd3",
    fontSize: "13px",
    fontFamily: "monospace",
    overflowWrap: "anywhere",
  },

  profileEmpty: {
    padding: "35px",
    textAlign: "center",
    color: "#777f8e",
  },

  profileTop: {
    display: "grid",
    gridTemplateColumns:
      "repeat(auto-fit, minmax(min(100%, 300px), 1fr))",
    gap: "12px",
    marginBottom: "14px",
  },

  statusText: {
    marginTop: "8px",
    fontSize: "18px",
    fontWeight: 800,
  },

  statusGood: {
    color: "#65eca0",
  },

  statusBad: {
    color: "#ff7187",
  },

  largeValue: {
    marginTop: "8px",
    fontSize: "19px",
    fontWeight: 800,
  },

  profileGrid: {
    display: "grid",
    gridTemplateColumns:
      "repeat(auto-fit, minmax(min(100%, 300px), 1fr))",
    gap: "10px",
  },

  detailsGrid: {
    display: "grid",
    gridTemplateColumns:
      "repeat(auto-fit, minmax(min(100%, 300px), 1fr))",
    gap: "10px",
  },

  dataBox: {
    background: "#0c0f15",
    border: "1px solid #1d222d",
    padding: "15px",
    borderRadius: "14px",
    minWidth: 0,
  },

  dataValue: {
    marginTop: "7px",
    fontWeight: 700,
    fontSize: "14px",
    overflowWrap: "anywhere",
  },

  claimBox: {
    display: "grid",
    gridTemplateColumns:
      "repeat(auto-fit, minmax(min(100%, 260px), 1fr))",
    alignItems: "center",
    gap: "20px",
    marginTop: "18px",
    padding: "20px",
    borderRadius: "18px",
    background:
      "linear-gradient(100deg,rgba(255,204,102,.14),rgba(20,22,31,.8))",
    border: "1px solid rgba(255,204,102,.32)",
  },

  claimBoxDisqualified: {
    background:
      "linear-gradient(100deg,rgba(255,73,105,.12),rgba(20,22,31,.9))",
    border: "1px solid rgba(255,113,135,.34)",
  },

  disqualifiedText: {
    color: "#ff7187",
  },

  claimStopped: {
    marginTop: "9px",
    color: "#a58b92",
    fontSize: "12px",
    lineHeight: 1.5,
    maxWidth: "560px",
  },

  claimLabel: {
    fontSize: "10px",
    color: "#828796",
    fontWeight: 800,
    letterSpacing: "1px",
  },

  claimTimer: {
    marginTop: "7px",
    fontSize: "22px",
    fontWeight: 850,
  },

  claimMeta: {
    marginTop: "6px",
    color: "#7c8291",
    fontSize: "11px",
  },

  claimReward: {
    display: "flex",
    flexDirection: "column",
  },

  rewardUsd: {
    marginTop: "6px",
    fontSize: "22px",
  },

  rewardKind: {
    color: "#8f95a4",
    marginTop: "4px",
    fontSize: "12px",
  },

  claimButton: {
    border: 0,
    borderRadius: "14px",
    padding: "14px 22px",
    color: "white",
    cursor: "pointer",
    fontWeight: 800,
    background: "linear-gradient(135deg,#ffcc66,#e5ad45)",
    width: "100%",
  },

  claimButtonDisabled: {
    opacity: 0.4,
    cursor: "not-allowed",
  },

  tierGrid: {
    display: "grid",
    gridTemplateColumns:
      "repeat(auto-fit, minmax(min(100%, 260px), 1fr))",
    gap: "10px",
  },

  tierCard: {
    borderRadius: "16px",
    padding: "18px",
    background: "#0c0f15",
    border: "1px solid #1e232e",
    minWidth: 0,
  },

  tierFeatured: {
    background:
      "linear-gradient(135deg,rgba(255,204,102,.20),rgba(17,20,28,1))",
    border: "1px solid #ffcc66",
  },

  tierPercent: {
    fontSize: "25px",
    fontWeight: 900,
  },

  tierRange: {
    color: "#9b9fad",
    fontSize: "12px",
    marginTop: "6px",
  },

  tierUsers: {
    color: "#ffcc66",
    marginTop: "13px",
    fontSize: "12px",
    fontWeight: 750,
  },

  totalBadge: {
    background: "#171b25",
    padding: "9px 13px",
    borderRadius: "12px",
    color: "#959bab",
    fontSize: "12px",
  },

  tableWrap: {
    width: "100%",
    overflowX: "auto",
    WebkitOverflowScrolling: "touch",
  },

  table: {
    width: "100%",
    borderCollapse: "separate",
    borderSpacing: "0 7px",
    minWidth: "950px",
  },

  th: {
    textAlign: "left",
    color: "#686f7e",
    fontSize: "10px",
    padding: "10px 12px",
    textTransform: "uppercase",
    letterSpacing: ".8px",
  },

  tr: {
    background: "#0b0e14",
  },

  td: {
    padding: "13px 12px",
    fontSize: "13px",
    borderTop: "1px solid #191e27",
    borderBottom: "1px solid #191e27",
  },

  rank: {
    width: "28px",
    height: "28px",
    borderRadius: "8px",
    display: "grid",
    placeItems: "center",
    background: "#171b25",
    fontWeight: 800,
  },

  addressText: {
    fontFamily: "monospace",
    color: "#c4c7d0",
  },

  rewardRateBadge: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: "54px",
    padding: "5px 8px",
    borderRadius: "8px",
    color: "#ffcc66",
    background: "rgba(255,204,102,.10)",
    border: "1px solid rgba(255,204,102,.25)",
    fontSize: "10px",
    fontWeight: 850,
  },

  rewardAmount: {
    color: "#ffcc66",
    fontWeight: 850,
  },

  tableStatus: {
    color: "#65eca0",
    fontSize: "11px",
    fontWeight: 800,
  },

  tableStatusBad: {
    color: "#ff7187",
  },

  tradeButton: {
    padding: "8px 12px",
    borderRadius: "9px",
    border: "1px solid #4a3a20",
    background: "#1d1810",
    color: "#ffcc66",
    cursor: "pointer",
    fontWeight: 700,
  },

  noRows: {
    textAlign: "center",
    padding: "35px",
    color: "#737987",
  },

  message: {
    position: "fixed",
    right: "20px",
    bottom: "20px",
    maxWidth: "400px",
    background: "#161a23",
    border: "1px solid #343a49",
    borderRadius: "13px",
    padding: "14px 17px",
    boxShadow: "0 15px 50px rgba(0,0,0,.4)",
    fontSize: "13px",
    zIndex: 30,
  },

  empty: {
    maxWidth: "520px",
    margin: "120px auto",
    textAlign: "center",
    background: "#10131a",
    border: "1px solid #212632",
    borderRadius: "22px",
    padding: "45px",
    color: "#969cab",
  },

  emptyIcon: {
    fontSize: "35px",
  },

  modalBackdrop: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,.78)",
    backdropFilter: "blur(8px)",
    display: "grid",
    placeItems: "center",
    padding: "20px",
    zIndex: 100,
  },

  modal: {
    width: "min(900px,100%)",
    maxHeight: "85vh",
    overflow: "hidden",
    background: "#0c0f15",
    border: "1px solid #2a3040",
    borderRadius: "22px",
    boxShadow: "0 30px 100px rgba(0,0,0,.6)",
  },

  modalHeader: {
    padding: "20px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    borderBottom: "1px solid #1d222c",
  },

  modalTitle: {
    margin: "5px 0 0",
  },

  closeButton: {
    width: "38px",
    height: "38px",
    borderRadius: "11px",
    background: "#171b24",
    color: "white",
    border: "1px solid #2d3340",
    cursor: "pointer",
    fontSize: "22px",
  },

  modalBody: {
    overflowY: "auto",
    maxHeight: "calc(85vh - 85px)",
    padding: "18px",
  },

  tradeCard: {
    background: "#11151c",
    border: "1px solid #202631",
    borderRadius: "15px",
    padding: "16px",
    marginBottom: "10px",
  },

  tradeTop: {
    display: "flex",
    justifyContent: "space-between",
    gap: "10px",
    flexWrap: "wrap",
    marginBottom: "14px",
  },

  verified: {
    color: "#66e89c",
    fontSize: "11px",
    fontWeight: 800,
  },

  tradeGrid: {
    display: "grid",
    gridTemplateColumns:
      "repeat(auto-fit, minmax(min(100%, 220px), 1fr))",
    gap: "12px",
  },

  tradeLabel: {
    color: "#6e7583",
    fontSize: "10px",
    textTransform: "uppercase",
  },

  tradeValue: {
    marginTop: "5px",
    fontWeight: 700,
    fontSize: "13px",
  },

  tradeId: {
    marginTop: "14px",
    color: "#59606e",
    fontFamily: "monospace",
    fontSize: "10px",
    overflowWrap: "anywhere",
  },
};